import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileText, Lock, LockKeyhole, Plus, Tags, Unlock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, statusOf } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useHub } from "@/components/AppShell";
import { EmptyState, Loader, NoAccess, PageHeader, StatusPill } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import { useLiveRefresh } from "@/lib/live";
import CodePad from "@/components/vault/CodePad";
import DocDialog from "@/components/vault/DocDialog";
import Viewer from "@/components/vault/Viewer";
import Filters, { EMPTY } from "@/components/documents/Filters";
import TagsDialog from "@/components/documents/TagsDialog";
import { StorageMeter } from "@/components/family/Storage";

const CYAN = "var(--glow-family)";

function Gate({ vbase, st, onToken, onClose }) {
  const [err, setErr] = useState(st.locked_until ? "Too many tries. Try again in 15 minutes." : null);
  const [first, setFirst] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = useCallback(async (code) => {
    if (!st.has_code && !first) { setFirst(code); setErr(null); return; }
    setBusy(true);
    try {
      const { data } = st.has_code ? await api.post(`${vbase}/unlock`, { code }) : await api.post(`${vbase}/code`, { code: first, confirm: code });
      onToken(data);
    } catch (e) { setErr(errMsg(e)); if (!st.has_code) setFirst(null); } finally { setBusy(false); }
  }, [vbase, st.has_code, first, onToken]);
  const forgot = () => api.post(`${vbase}/forgot`).then(() => { setSent(true); toast.success("Check your email for a link to reset your vault code"); }).catch((e) => toast.error(errMsg(e)));
  const title = st.has_code ? "Enter your vault code" : first ? "Enter it again" : "Set your vault code";
  const hint = st.has_code ? "Sensitive documents need your own 6-digit code, separate from your password." : "Choose 6 digits. Only you use this code.";
  return (
    <FormDialog open onClose={onClose} title="Unlock sensitive documents" description="Identity, Insurance, Medical and Financial/Tax" testid="vault-gate">
      <CodePad key={title} title={title} hint={hint} error={err} busy={busy} onSubmit={submit}
        footer={st.has_code && <button className="mt-5 min-h-[44px] text-sm font-semibold text-q-muted underline hover:text-q-text" disabled={sent} onClick={forgot} data-testid="vault-forgot-btn">{sent ? "Reset link sent" : "Forgot vault code"}</button>} />
    </FormDialog>
  );
}

function DocCard({ x, onOpen }) {
  const pill = x.expired ? ["Expired", "var(--glow-media)"] : x.expiring_soon ? [`Expires in ${x.days_left} day${x.days_left === 1 ? "" : "s"}`, "var(--glow-lists)"] : null;
  return (
    <button className="q-card q-rise min-h-[44px] min-w-0 p-4 text-left transition-transform duration-150 hover:-translate-y-0.5" onClick={onOpen} data-testid={`doc-${x.id}`}>
      <div className="flex items-start gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: `rgba(${CYAN},0.14)` }}>
          {x.sensitive ? <LockKeyhole className="h-5 w-5" style={{ color: `rgb(${CYAN})` }} /> : <FileText className="h-5 w-5" style={{ color: `rgb(${CYAN})` }} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-q-text">{x.title}</p>
          <p className="truncate text-xs text-q-muted" data-testid={`doc-meta-${x.id}`}>{x.category}{x.sensitive ? " · sensitive" : ""} · {[...x.belongs_names, ...x.links.map((l) => l.name)].join(", ")}</p>
          {x.number_masked && <p className="mt-1 font-mono text-sm text-q-text" data-testid={`doc-masked-${x.id}`}>{x.number_masked}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {pill && <StatusPill label={pill[0]} rgb={pill[1]} testid={`doc-expiry-${x.id}`} />}
            {x.tags.map((t) => <span key={t.id} className="rounded-full bg-q-raised px-2 py-0.5 text-xs font-semibold text-q-text" data-testid={`doc-tag-${x.id}-${t.id}`}>{t.name}</span>)}
            <span className="text-xs text-q-muted">{x.files.length} file{x.files.length === 1 ? "" : "s"}{x.expiry ? ` · expires ${fmtDate(x.expiry)}` : ""}{x.my_mode === "view" ? " · view only" : ""}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function LockBar({ d, canUnlock, onUnlock, onLock }) {
  if (d.unlocked) return <button className="q-btn q-btn-secondary min-h-[44px]" onClick={onLock} data-testid="vault-lock-btn"><Lock className="h-4 w-4" /> Lock sensitive</button>;
  if (!canUnlock) return null;
  return (
    <button className="q-btn q-btn-secondary min-h-[44px]" onClick={onUnlock} data-testid="vault-unlock-btn">
      <Unlock className="h-4 w-4" /> Unlock sensitive{d.locked_count ? ` (${d.locked_count})` : ""}
    </button>
  );
}

export default function Documents() {
  const { hub } = useHub();
  const base = `/organizations/${hub.id}`;
  const vbase = `${base}/vault`;
  const [st, setSt] = useState(null);
  const [denied, setDenied] = useState(false);
  const [tok, setTok] = useState(null);
  const [gate, setGate] = useState(false);
  const [d, setD] = useState(null);
  const [flt, setFlt] = useState(EMPTY);
  const [open, setOpen] = useState(() => new URLSearchParams(window.location.search).get("doc"));
  const [edit, setEdit] = useState(null);
  const [tags, setTags] = useState(false);
  const timer = useRef(null);
  const headers = useMemo(() => (tok ? { "X-Vault-Token": tok.token } : {}), [tok]);
  const status = useCallback(() => api.get(`${vbase}/status`).then(({ data }) => setSt(data)).catch((e) => (statusOf(e) === 403 ? setDenied(true) : toast.error(errMsg(e)))), [vbase]);
  const load = useCallback(() => api.get(`${base}/documents`, { headers, params: flt }).then(({ data }) => setD(data)).catch((e) => {
    if (statusOf(e) === 403) setDenied(true); else if (statusOf(e) !== 401) toast.error(errMsg(e));
  }), [base, headers, flt]);
  const lock = useCallback((msg) => {
    setTok((t) => { if (t) api.post(`${vbase}/lock`, null, { headers: { "X-Vault-Token": t.token } }).catch(() => {}); return null; });
    setOpen(null); setEdit(null); status();
    if (msg) toast(msg);
  }, [vbase, status]);
  useEffect(() => { status(); }, [status]);
  useEffect(() => { const t = setTimeout(load, flt.q ? 250 : 0); return () => clearTimeout(t); }, [load, flt.q]);
  const lockRef = useRef(lock);
  lockRef.current = lock;
  useEffect(() => {
    const id = api.interceptors.response.use((r) => r, (err) => {
      if (err.response?.data?.code === "vault_locked") lockRef.current("Sensitive documents locked");
      return Promise.reject(err);
    });
    return () => api.interceptors.response.eject(id);
  }, []);
  useEffect(() => () => { setTok((t) => { if (t) api.post(`${vbase}/lock`, null, { headers: { "X-Vault-Token": t.token } }).catch(() => {}); return null; }); }, [vbase]);
  useEffect(() => {
    if (!tok) return undefined;
    const idle = (tok.idle_seconds || 300) * 1000;
    const reset = () => { clearTimeout(timer.current); timer.current = setTimeout(() => lock("Sensitive documents locked after 5 minutes idle"), idle); };
    reset();
    const evs = ["pointerdown", "keydown", "scroll"];
    evs.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => { clearTimeout(timer.current); evs.forEach((e) => window.removeEventListener(e, reset)); };
  }, [tok, lock]);
  useLiveRefresh(load, (m) => m.entity === "documents", 20000);
  if (denied) return <NoAccess />;
  if (!st || !d) return <Loader />;
  const owner = d.mode === "parent";
  const isKid = !d.people;
  const soon = d.documents.filter((x) => x.expiring_soon || x.expired).sort((a, b) => a.days_left - b.days_left);
  const filtered = Object.keys(EMPTY).some((k) => flt[k] !== EMPTY[k]);
  return (
    <div data-testid="documents-page" className="min-w-0">
      <PageHeader eyebrow="Family" title="Documents" rgb={CYAN} testid="documents-title" />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {owner && <button className="q-btn q-btn-primary min-h-[44px]" onClick={() => setEdit({})} data-testid="doc-add-btn"><Plus className="h-4 w-4" /> Add document</button>}
        {owner && <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => setTags(true)} data-testid="doc-tags-btn"><Tags className="h-4 w-4" /> Family tags</button>}
        {!isKid && <LockBar d={d} canUnlock={owner || d.locked_count > 0} onUnlock={() => setGate(true)} onLock={() => lock()} />}
      </div>
      {owner && <StorageMeter key={d.documents.length} hubId={hub.id} compact />}
      {!d.unlocked && d.locked_count > 0 && <p className="mb-4 text-sm text-q-muted" data-testid="documents-locked-note">{d.locked_count} sensitive document{d.locked_count === 1 ? " is" : "s are"} hidden until you unlock with your vault code.</p>}
      {soon.length > 0 && !filtered && (
        <section className="mb-5 rounded-2xl border p-4" style={{ borderColor: "rgba(var(--glow-lists),0.4)" }} data-testid="vault-expiring">
          <p className="mb-2 text-xs font-black uppercase tracking-[0.18em]" style={{ color: "rgb(var(--glow-lists))" }}>Expiring soon</p>
          <ul className="space-y-1 text-sm">{soon.map((x) => <li key={x.id}><button className="min-h-[44px] text-left text-q-text underline-offset-2 hover:underline" onClick={() => setOpen(x.id)} data-testid={`expiring-${x.id}`}>{x.title} · {x.days_left < 0 ? "expired" : x.days_left === 0 ? "expires today" : `in ${x.days_left} days`} ({fmtDate(x.expiry)})</button></li>)}</ul>
        </section>
      )}
      <Filters base={base} d={d} flt={flt} setFlt={setFlt} isKid={isKid} />
      {!d.documents.length ? <EmptyState title={filtered ? "No documents match" : "No documents yet"} hint={owner && !filtered ? "Add passports, insurance cards, manuals and school forms. Files are encrypted." : undefined} rgb={CYAN} testid="vault-empty" /> : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="vault-docs">{d.documents.map((x) => <DocCard key={x.id} x={x} onOpen={() => setOpen(x.id)} />)}</div>
      )}
      {gate && <Gate vbase={vbase} st={st} onClose={() => setGate(false)} onToken={(t) => { setTok(t); setGate(false); }} />}
      {open && <Viewer base={base} headers={headers} d={d} id={open} onClose={() => setOpen(null)} onEdit={(doc) => { setOpen(null); setEdit(doc); }} onChanged={load} />}
      {edit && <DocDialog base={base} headers={headers} d={d} doc={edit.id ? edit : null} onUnlock={() => setGate(true)} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {tags && <TagsDialog base={base} d={d} onClose={() => setTags(false)} onChanged={load} />}
    </div>
  );
}
